'use client';

import type { ReactNode } from 'react';
import { styled } from 'styled-components';
import cs from 'classnames';
import type { IconNameType } from '@wistia/ui';
import { Icon } from '@wistia/ui';
import BaseText from '@dvd/components/BaseText';
import Media from '@dvd/components/Media';
import type { ImageProps, Orientation } from '@types';

export type SplitThreeUpItem = {
  /**
   * Optional icon shown with the heading. Give all three items an icon or
   * none, so the headings line up.
   */
  icon?: IconNameType;
  /**
   * What the item’s H3 says
   */
  heading: string;
  /**
   * Descriptive paragraph
   */
  description: ReactNode | string;
};

/**
 * One image or video for a media frame, discriminated on `kind`, per
 * `docs/adrs/20260501-mutually-exclusive-component-props.md`. Matches the
 * sanctioned shape used by `FeatureShowcase`; the type system guards which
 * fields are read for each media, so the module needs no runtime prop checks.
 */
export type SplitThreeUpMedia =
  ({ kind: 'image' } & ImageProps) | { kind: 'video'; hashedId: string };

/**
 * Discriminated on `variant`, per
 * `docs/adrs/20260501-mutually-exclusive-component-props.md`: only `narrow`
 * takes a second media, and it must.
 */
type SplitThreeUpVariantProps =
  | {
      /**
       * `default` pairs shorter copy with one 16:9 media; `narrow` gives
       * longer copy a wider column and adds 4:3 media for the side-by-side
       * layout. See the module’s usage notes.
       */
      variant?: 'default';
    }
  | {
      variant: 'narrow';
      /**
       * `narrow` only: the 4:3 image or video shown side by side, from the
       * `large` breakpoint up. The main media is 16:9 and shows while stacked.
       */
      desktopMedia: SplitThreeUpMedia;
    };

type SplitThreeUpProps = SplitThreeUpVariantProps & {
  /**
   * Which side the media sits on from the `large` breakpoint up.
   */
  mediaOrientation: Orientation;
  /**
   * The 16:9 image or video. Shown at every viewport in `default`; in
   * `narrow` it shows only while stacked, with `desktopMedia` side by side.
   */
  media: SplitThreeUpMedia;
  /**
   * Exactly three items
   */
  items: [SplitThreeUpItem, SplitThreeUpItem, SplitThreeUpItem];
};

const GridLayout = styled.div`
  display: grid;
  margin-block: ${({ theme }) => theme.spacing[3]};
  row-gap: ${({ theme }) => theme.spacing[2]};
  ${({ theme }) => theme.grid.columns.small};

  ${({ theme }) => theme.mq.minWidth.large} {
    align-items: center;
    margin-block: ${({ theme }) => theme.spacing[4]};
    ${({ theme }) => theme.grid.columns.large};
  }
`;

const MediaFrame = styled.div`
  aspect-ratio: 16 / 9;
  border-radius: ${({ theme }) => theme.radius.std};
  grid-column: 1 / -1;
  grid-row: 1;
  overflow: hidden;

  // Media renders the img directly here; object-fit only applies to it, and
  // the video branch (a Player wrapper div) already fills the frame via
  // fitStrategy="cover", so this rule is just to cover the image
  & > img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  // narrow has a frame per layout; display, not visibility, so the one that
  // is out of layout leaves the a11y tree too and only one media is announced
  &.sideBySideOnly {
    display: none;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    &.sideBySideOnly {
      display: block;
    }

    &.stackedOnly {
      display: none;
    }

    ${GridLayout}.narrow & {
      aspect-ratio: 4 / 3;
    }

    ${GridLayout}.narrow.start & {
      grid-column: 1 / span 6;
    }

    ${GridLayout}.narrow.end & {
      grid-column: 7 / span 6;
    }

    ${GridLayout}.default.start & {
      grid-column: 1 / span 8;
    }

    ${GridLayout}.default.end & {
      grid-column: 5 / span 8;
    }
  }
`;

const Items = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[1.5]};
  grid-column: 1 / -1;
  grid-row: 2;

  ${({ theme }) => theme.mq.minWidth.small} {
    grid-template-columns: repeat(3, 1fr);
    column-gap: ${({ theme }) => theme.spacing[1]};
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    grid-row: 1;
    grid-template-columns: 1fr;
    row-gap: ${({ theme }) => theme.spacing[3]};

    ${GridLayout}.narrow.start & {
      grid-column: 8 / span 5;
    }

    ${GridLayout}.narrow.end & {
      grid-column: 1 / span 5;
    }

    ${GridLayout}.default.start & {
      grid-column: 10 / span 3;
    }

    ${GridLayout}.default.end & {
      grid-column: 1 / span 4;
    }
  }
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[0.75]};

  .itemIcon {
    color: ${({ theme }) => theme.colors.blue500};
    flex-shrink: 0;
  }

  // bare selector because BaseText renders the paragraph; its global bottom
  // margin would otherwise add to the gap between items
  p:last-child {
    margin-block-end: 0;
  }

  // only an icon sits beside the text; without one the text keeps the
  // column's full width
  ${({ theme }) => theme.mq.minWidth.large} {
    ${GridLayout}.default &:has(> .itemIcon) {
      flex-direction: row;
    }
  }
`;

// one renderer for both frames; only the asset differs. `kind` guards which
// props Media receives, so no runtime hashedId check is needed
const renderMedia = (media: SplitThreeUpMedia) =>
  media.kind === 'video' ? (
    <Media fitStrategy="cover" hashedId={media.hashedId} />
  ) : (
    <Media
      alt={media.alt}
      height={media.height}
      src={media.src}
      width={media.width}
    />
  );

/**
 * Media beside three items, each an H3 and a paragraph, optionally led by an
 * icon.
 * Stacked below the `large` breakpoint — media on top, items in a row — and
 * side by side from there, with `mediaOrientation` choosing the media’s side.
 *
 * **Usage Notes**: the two variants keep copy length and media size in
 * balance; choosing between them is a designer’s judgment call. Both take a
 * 16:9 main media.
 *
 * - `default` suits shorter copy. The items take four columns and the one
 *   media holds 16:9 at every viewport.
 * - `narrow` suits longer copy. The items take five columns, and a required
 *   4:3 `desktopMedia` replaces the main media side by side.
 */
const SplitThreeUp = (props: SplitThreeUpProps) => {
  const { items, media, mediaOrientation, variant = 'default' } = props;
  // desktopMedia lives only on the `narrow` branch of the union, so narrow on
  // `variant` to read it rather than a `?: never` on the default branch
  const desktopMedia =
    props.variant === 'narrow' ? props.desktopMedia : undefined;

  return (
    <GridLayout className={cs(variant, mediaOrientation)}>
      <MediaFrame className={cs({ stackedOnly: Boolean(desktopMedia) })}>
        {renderMedia(media)}
      </MediaFrame>
      {desktopMedia && (
        <MediaFrame className="sideBySideOnly">
          {renderMedia(desktopMedia)}
        </MediaFrame>
      )}
      <Items>
        {items.map(({ icon, heading, description }) => (
          <Item key={heading}>
            {icon && <Icon className="itemIcon" size="lg" type={icon} />}
            <BaseText
              body={description}
              bodySize="bodyMd"
              heading={heading}
              headingLevel="h3"
            />
          </Item>
        ))}
      </Items>
    </GridLayout>
  );
};

export default SplitThreeUp;
